import { Box, Button, Card, CardActions, CardContent, Stack, Tooltip, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useGetDeviceLicsQuery, useUpdateDeviceLicsMutation } from "services/data/deviceApi";
import styles from "./Lics.module.css";
import { isEqual } from "lodash";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import { AccessTime, AppsOutlined, Done, HideSource, PhonelinkErase, PhonelinkEraseOutlined, SaveOutlined, ShoppingBagOutlined } from "@mui/icons-material";
import { DialogActionItem } from "components/grid";
import { HidingError } from "components/errors";
import { isoToDateString, monthEnding } from "utils/date";
import StrippedDataGrid from "components/styled";

const licNameRenderer = ({value}) => {
  return ( <div className={styles.licName}>
            <div className={styles.duration}>{value.duration} {monthEnding(value.duration)}</div>
            <div className={styles.key}>{value.key}</div>
          </div>
  );
};



const getRowClassName = ({row, indexRelativeToCurrentPage }) => {
  let result = '';
  row.isBlock && (result = result + ' disabled ');
  result = result + (indexRelativeToCurrentPage % 2 === 0 ? ' even' : ' odd');
  row.isDelete && (result = result + ' deleted');
  return result;
};

const DEFAULT_ERROR = "Ошибка соединения с сервером";

const Lics = () => {
    const { id } = useParams();
    const { data, error, isLoading } = useGetDeviceLicsQuery({id});
    const [updateDeviceLics, {error: updateDeviceLicsError, isLoading: isUpdatingDeviceLics, reset: updateDeviceLicsReset}] = useUpdateDeviceLicsMutation();
    const [items, setItems] = useState(data?.body?.items);
  
    useEffect(() => {
      setItems(data?.body?.items);
    }, [data]);
  
    const setDisabled = useCallback( (row) => {
      const index = items.findIndex(el => el.id === row.id);
      if(index !== -1) {
        setItems( (old) => [...old.slice(0, index), {...row, isBlock: !row?.isBlock}, ...old.slice(index + 1)]);
      }
    }, [data, items]);

    const unlinkLic = useCallback( (row) => {
      const index = items.findIndex(el => el.id === row.id);
      if(index !== -1) {
        if(!row.isDelete) {
          setItems( (old) => [...old.slice(0, index), {...row, isDelete: true}, ...old.slice(index + 1)]);
        } 
        else {
          const {isDelete, ...others} = row;
          setItems( (old) => [...old.slice(0, index), {...others}, ...old.slice(index + 1)]);
        }
      }
    }, [data, items]);
  
    const isSaveButtonDisabled = useCallback( () => isEqual(data?.body?.items, items), [data, items]);
    
    const hideError = () => {
      if(updateDeviceLicsError) updateDeviceLicsReset();
    };
    
    const openError = () => { 
      return !!updateDeviceLicsError || false;
    };
  
    const getErrorMessage = () => { 
      if(updateDeviceLicsError) return updateDeviceLicsError?.data?.message ?? DEFAULT_ERROR;
    };

    const renderApp = (item) => {
      return (
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <AppsOutlined sx={{mx: 1}} />
          <Box sx={{display: 'flex', flexDirection: 'column'}}>
            <Typography align='center'>{item.subject.at(0).name}</Typography>
            <Typography variant="caption">{item.subject.at(0).package}</Typography>
          </Box>
        </Box>
      );
    };
  
    const renderProfile = (item) => {
      return (
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <ShoppingBagOutlined sx={{mx: 1}}/>
          <Typography>Профиль {item.subject.at(0).name}</Typography>
        </Box>
      );
    };
  
    const renderSubject = ({value}) => {
      return (
        <div className={styles.subject}>{value.subjectType === 'app' ? renderApp(value) : renderProfile(value)}</div>
      );
    };

    const licStatusTextPrefix = (value) => {
      return value?.activatedAt ? 'Активирована' : 'Активировать до'
    };

    const licStatusText = (value) => {
      return value?.activatedAt ? isoToDateString(value.activatedAt) : isoToDateString(value.endActivationAt); 
    };
    
    const saveChanges = () => {
      updateDeviceLics({devId: id, body: items.filter(el => !el.isDelete)});
    };

    const renderDate = ({value}) => {
      return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{width: '100%'}}>
          <AccessTime fontSize="small" color='secondary'/>
          <Stack sx={{width: '100%'}}>
            <Typography variant="caption" sx={{lineHeight: "1.2rem"}}>Загружена <span style={{float:"right"}}>{isoToDateString(value.usedAt)}</span></Typography>
            <Typography variant="caption" sx={{lineHeight: "1.2rem"}}>{licStatusTextPrefix(value)} <span style={{float:"right"}}>{licStatusText(value)}</span></Typography>
          </Stack>
          
        </Stack>
      );
    };
  
    const columns = useMemo( () => [
        {field: 'name', width: 160, valueGetter: (value, row) => ({key: row.key, duration: row.duration}), renderCell: licNameRenderer, display: 'flex'},
        {field: 'subject', flex: 1, valueGetter: (value, row) => ({subject: row.subject, subjectType: row.subjectType}), renderCell: renderSubject, display: 'flex'},
        {field: 'activatedAt', valueGetter: (value, row) => ({activatedAt: row?.activatedAt, endActivationAt: row?.endActivationAt, usedAt: row.usedAt}), 
            renderCell: renderDate, display: 'flex', flex: 1},
        {field: 'actions', type: 'actions', align: "right",  getActions: ({row}) => [
          <DialogActionItem
            icon={ row.isBlock ?  <Tooltip title="Разблокировать лицензию"><Done /></Tooltip> : <Tooltip title="Заблокировать лицензию"><HideSource /></Tooltip>}
            label={ row.isBlock ? "Разблокировать" : "Заблокировать" }
            handler={()=>setDisabled(row)}
            title={row.isBlock ? 'Разблокировать лицензию': 'Заблокировать лицензию'}
          >
            <span>{row.isBlock ? 'Разблокировать' : 'Заблокировать'} лицензию <b>{row.key}</b> в кассе <b>{id}</b>?</span>                  
          </DialogActionItem>,
          <DialogActionItem
            disabled={!!row.activatedAt}
            icon={ <Tooltip title="Отвязать лицензию от кассы"><PhonelinkErase /></Tooltip>}
            label="Отвязать лицензию"
            handler={()=>unlinkLic(row)}
            title={'Отвязать лицензию от кассы'}
          >
            <span>Отвязать неактивированную лицензию <b>{row.key}</b> от кассы <b>{id}</b>?</span>                  
          </DialogActionItem>
          
        ]}  
      ], [items]
    );
  
    return (
      <Card sx={{maxHeight: '100%', display: 'flex', flexDirection: 'column'}}>
        <HidingError 
            open={ openError() }
            message={ getErrorMessage() }
            onClose={hideError}
          /> 
        <CardContent sx={{overflow: 'auto'}}>
          <StrippedDataGrid
            className={styles.apps}
            rowHeight={60}
            isLoading={isLoading}
            getRowClassName={ getRowClassName }
            rows={items}
            localeText={{noRowsLabel: error?.data?.message ?? "Лицензий пока нет"}}
            columns={columns}
            columnHeaderHeight={1}
            disableColumnResize
            disableColumnMenu
            disableRowSelectionOnClick
            disableMultipleRowSelection
            hideFooter
            sx={{
              width: '100%',
              [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
                outline: 'none',
              },
              [`& .${gridClasses.columnHeader}, & .MuiInputBase-root`]: {
                overflowX: 'unset'
              }
            }}
          />
        </CardContent>
        <CardActions sx={{px: 2}}>
          <Button
            fullWidth
            disabled={isSaveButtonDisabled()}
            startIcon={<SaveOutlined/>}
            color='error'
            variant='contained'
            sx={{my: 1}}
            onClick={saveChanges}
          >Сохранить</Button>    
        </CardActions>
      </Card>
        
    );
};

export default Lics;